import { useId, type ReactNode } from 'react';
import type { CurrentUser } from '@quire/shared';
import { nextTheme, useTheme, type Theme } from '../lib/theme';
import { Popover } from './Popover';

/*
 * Spoken form. "Always" against "follow" is the distinction worth carrying:
 * one of the three steps in the cycle changes the preference without changing
 * a single pixel - leaving System while it happens to be resolving light, for
 * a pinned Light. Announcing that as "currently light, switch to light" is
 * accurate and useless; naming the pinning is what tells you what you got.
 */
const SPOKEN: Record<Theme, string> = {
  light: 'always light',
  dark: 'always dark',
  system: 'follow the system',
};

/*
 * Drawn as SVG rather than set as ☀/☾ glyphs. The bundled faces are Archivo
 * and IBM Plex Mono - neither promises pictographs, so a glyph here is one
 * missing codepoint away from a tofu box in the corner of every screen.
 */
const ICON: Record<Theme, ReactNode> = {
  light: (
    <>
      <circle cx="12" cy="12" r="4.25" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4" />
    </>
  ),
  dark: <path d="M20.5 14.8A8.7 8.7 0 0 1 9.2 3.5a8.7 8.7 0 1 0 11.3 11.3Z" />,
  system: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      {/* The filled half - the conventional "auto" mark. */}
      <path d="M12 3.5a8.5 8.5 0 0 1 0 17Z" fill="currentColor" stroke="none" />
    </>
  ),
};

/**
 * Who is signed in, and everything that is theirs to change: the theme,
 * which organization to work inside, signing out. Opened from the name in
 * the masthead.
 *
 * The identity block at the top is what names the menu, so opening it
 * announces who is signed in and where before a single item is read. It is
 * also the one place the app shows the address or username a person signed
 * in with.
 *
 * The theme row does not close the menu. It cycles rather than picks, and
 * one of its three steps changes nothing on screen (see SPOKEN), so the row
 * stays under the pointer to be read again and clicked again.
 *
 * Switching and signing out close first and act second. Signing out unmounts
 * the shell, and switching leaves it for the picker; either way the menu has
 * no business still being open when the page changes underneath it.
 */
export function UserMenu({
  user,
  onSignOut,
  onSwitchOrganization,
  signingOut,
}: {
  user: CurrentUser;
  onSignOut: () => void;
  /**
   * Takes a platform administrator to the organization picker. Only
   * navigation: the picker is where the session's scope actually changes.
   */
  onSwitchOrganization: () => void;
  signingOut: boolean;
}) {
  const { theme, resolved, setTheme } = useTheme();
  const next = nextTheme(theme);
  const identityId = useId();

  // A platform administrator chooses which organization to work inside, and
  // can choose again. Everyone else is pinned to their own.
  const isPlatform = user.role === 'super_admin';

  const current = theme === 'system' ? `${SPOKEN.system}, currently ${resolved}` : SPOKEN[theme];
  const description = `Theme: ${current}. Switch to ${SPOKEN[next]}.`;
  // The same sentence over two short lines: the setting, then what it comes
  // to and where a click goes. Rendered so "follow the system" does not wrap
  // in the middle of the row's first line.
  const hint =
    theme === 'system'
      ? `Currently ${resolved}, switch to ${SPOKEN[next]}`
      : `Switch to ${SPOKEN[next]}`;
  // A platform account made with only a username often has it as its name
  // too, and "admin" under "admin" says nothing.
  const identifier = user.email ?? user.username;
  const signedInAs = identifier === user.name ? null : identifier;

  return (
    <Popover
      role="menu"
      labelledBy={identityId}
      trigger={
        <>
          {/* Shorter on a phone, where the organization's name shares the
              row and would otherwise be the one to give way. */}
          <span className="max-w-[6rem] truncate sm:max-w-[10rem]">{user.name}</span>
          <Chevron />
        </>
      }
      // `group`, so the chevron can turn on aria-expanded without the menu
      // having to say whether it is open.
      triggerClassName="group flex items-center gap-1.5 rounded px-2 py-1.5 text-sm text-ink-soft transition-colors hover:bg-paper-sunk hover:text-ink aria-expanded:bg-paper-sunk aria-expanded:text-ink"
      panelClassName="w-64"
    >
      {({ close }) => (
        <>
          <div id={identityId} className="grid gap-0.5 px-2 py-1.5">
            <span className="truncate text-sm font-semibold">{user.name}</span>
            {signedInAs ? (
              <span className="truncate text-xs text-ink-faint">{signedInAs}</span>
            ) : null}
            <span className="truncate text-xs text-ink-soft">
              {user.organization?.name ?? 'Platform'}
            </span>
          </div>
          <hr className="my-1 border-rule" />
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            // The two lines below say the same thing; this is the one sentence.
            aria-label={description}
            onClick={() => setTheme(next)}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="size-[18px] shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {ICON[theme]}
            </svg>
            <span className="grid">
              <span>Theme: {SPOKEN[theme]}</span>
              <span className="text-xs text-ink-faint">{hint}</span>
            </span>
          </button>
          <hr className="my-1 border-rule" />
          {isPlatform ? (
            <button
              type="button"
              role="menuitem"
              className="menu-item"
              onClick={() => {
                close();
                onSwitchOrganization();
              }}
            >
              {user.organization ? 'Switch organization' : 'Enter an organization'}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={signingOut}
            onClick={() => {
              close();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </>
      )}
    </Popover>
  );
}

/** Points down closed and up open. The same stroke the ledger's rows use. */
function Chevron() {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden="true"
      focusable="false"
      className="rotate-90 transition-transform group-aria-expanded:-rotate-90 print:hidden"
    >
      <path
        d="M3 1 L7 5 L3 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
